import { useState, useEffect, useCallback } from "react";

// Track dropdown state globally
let openDropdowns = 0;
const dropdownListeners = new Set<(open: boolean) => void>();

export function notifyDropdownChange() {
  const hasOpenDropdowns = openDropdowns > 0;
  dropdownListeners.forEach((listener) => listener(hasOpenDropdowns));
}

export function registerDropdownListener(listener: (open: boolean) => void) {
  dropdownListeners.add(listener);
  return () => {
    dropdownListeners.delete(listener);
  };
}

export function incrementDropdowns() {
  openDropdowns++;
  notifyDropdownChange();
}

export function decrementDropdowns() {
  openDropdowns = Math.max(0, openDropdowns - 1);
  notifyDropdownChange();
}

export function getOpenDropdowns() {
  return openDropdowns;
}

export function getDeviceLabel(d: MediaDeviceInfo | undefined, fallback: string) {
  return d?.label?.trim() || fallback;
}

export function playTestTone() {
  try {
    type WK = Window & { webkitAudioContext?: typeof AudioContext };
    const AudioCtor = window.AudioContext ?? (window as unknown as WK).webkitAudioContext;
    if (!AudioCtor) return;
    const ctx = new AudioCtor();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.08;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    window.setTimeout(() => {
      osc.stop();
      ctx.close().catch(() => {});
    }, 250);
  } catch {
    // ignore
  }
}

export function formatDuration(meetingStartTime: Date | undefined, now: number) {
  const meetingDuration = meetingStartTime
    ? Math.floor((now - meetingStartTime.getTime()) / 1000)
    : 0;
  const durationHours = Math.floor(meetingDuration / 3600);
  const durationMinutes = Math.floor((meetingDuration % 3600) / 60);
  const durationSeconds = meetingDuration % 60;
  return durationHours > 0
    ? `${durationHours}:${String(durationMinutes).padStart(2, "0")}:${String(durationSeconds).padStart(2, "0")}`
    : `${String(durationMinutes).padStart(2, "0")}:${String(durationSeconds).padStart(2, "0")}`;
}

export function useMicReminder() {
  const [micReminderOpen, setMicReminderOpen] = useState(false);
  const [micOffCount, setMicOffCount] = useState(0);
  const [micReminderDismissed, setMicReminderDismissed] = useState(false);

  const handleDismissMicReminder = useCallback(() => {
    setMicReminderOpen(false);
    setMicReminderDismissed(true);
  }, []);

  // Auto-close mic reminder after 5 seconds
  useEffect(() => {
    if (micReminderOpen) {
      const timer = setTimeout(() => {
        setMicReminderOpen(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [micReminderOpen]);

  return {
    micReminderOpen,
    setMicReminderOpen,
    micOffCount,
    setMicOffCount,
    micReminderDismissed,
    handleDismissMicReminder,
  };
}

export function injectMicBarsAnimation() {
  if (typeof document !== "undefined") {
    const existingStyle = document.head.querySelector("style[data-mic-bars]");
    if (!existingStyle) {
      const style = document.createElement("style");
      style.setAttribute("data-mic-bars", "true");
      style.textContent = `
        @keyframes micBars {
          0% { transform: scaleY(0.45); opacity: 0.55; }
          100% { transform: scaleY(1); opacity: 1; }
        }
        @keyframes waveform {
          0% { transform: scaleY(0.3); }
          50% { transform: scaleY(0.8); }
          100% { transform: scaleY(1.2); }
        }
      `;
      document.head.appendChild(style);
    }
  }
}
